/**
 * Copyright 2024-2026 Tesenso AG
 *
 * Partner Auth Client
 *
 * Session handling for independent partner accounts (own login system,
 * separate from TB users). The opaque session token is kept in localStorage
 * and sent as X-Partner-Auth header.
 */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';

const TOKEN_KEY = 'tesenso_partner_token';
const NAME_KEY = 'tesenso_partner_name';

export interface PartnerLoginResponse {
  token: string;
  email: string;
  firstName?: string;
  lastName?: string;
  partnerName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PartnerAuthClientService {

  private readonly API = '/api/noauth/tesenso/partner';

  constructor(private http: HttpClient) {}

  get token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  get partnerName(): string | null {
    return localStorage.getItem(NAME_KEY);
  }

  get isLoggedIn(): boolean {
    return !!this.token;
  }

  authHeaders(): HttpHeaders {
    return new HttpHeaders(this.token ? { 'X-Partner-Auth': this.token } : {});
  }

  login(email: string, password: string): Observable<PartnerLoginResponse> {
    return this.http.post<PartnerLoginResponse>(`${this.API}/login`, { email, password }).pipe(
      tap(response => {
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(NAME_KEY, response.partnerName || '');
      })
    );
  }

  logout(): Observable<any> {
    const request = this.http.post(`${this.API}/logout`, {}, { headers: this.authHeaders() });
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(NAME_KEY);
    return request;
  }

  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(NAME_KEY);
  }

  register(payload: any): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API}/register`, payload);
  }

  activate(token: string, password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API}/activate`, { token, password });
  }

  requestReset(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API}/request-reset`, { email });
  }

  reset(token: string, password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API}/reset`, { token, password });
  }
}
